import React, { useState } from 'react';
import { getFromStorage, clearSecureStorage, saveToStorage } from '../utils/storage';
import { decryptData } from '../utils/crypto';
import Wallet from 'ethereumjs-wallet';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Container, Box, Typography, Card, CardContent, Button, TextField, AppBar, Toolbar,
  Menu, MenuItem, IconButton, Fade, Chip, Stack, ListItemIcon, ListItemText,
  CircularProgress, Avatar, Divider, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, Tab, Tabs, styled
} from '@mui/material';
import {
  Wallet as WalletIcon, Public, People, Security, Lock, Menu as MenuIcon, 
  ContentCopy, Key, Save, ArrowBack, CloudDownload, VerifiedUser, ErrorOutline,
  Visibility, VisibilityOff, VpnKey, FormatQuote, TwoWheeler, Delete, ExitToApp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';

// Styled card component with glassmorphism effect
const GlassCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  backgroundColor: '#1a1a1a',
  border: '1px solid #333333',
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.4)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.5)'
  }
}));

// Styled button component with monochromatic gradient
const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(90deg, #ffffff 0%, #cccccc 100%)',
  backgroundSize: '200% auto',
  color: '#000000',
  borderRadius: '12px',
  padding: '12px 24px',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundPosition: 'right center',
    boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',
    transform: 'translateY(-2px)'
  },
  '&:active': {
    transform: 'translateY(0)'
  },
  '&.Mui-disabled': {
    background: '#333333',
    color: '#666666',
    boxShadow: 'none'
  }
}));

// Dashboard card component
const DashboardCard = ({ children, sx = {} }) => (
  <GlassCard 
    elevation={0} 
    sx={{
      p: 3,
      minHeight: '100%',
      ...sx
    }}
  >
    <CardContent sx={{ p: 0, height: '100%' }}>
      {children}
    </CardContent>
  </GlassCard>
);

function ExportBackup({ wallet }) {
  const [activeTab, setActiveTab] = useState(0);
  const [backupData, setBackupData] = useState('');
  const [password, setPassword] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showSeedPhrase, setShowSeedPhrase] = useState(false);
  const [show2FACode, setShow2FACode] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [decryptedData, setDecryptedData] = useState(null);
  const navigate = useNavigate();

  const validatePassword = async () => {
    try {
      // Fetch and decrypt cryptoWallet
      const encryptedWallet = await getFromStorage('cryptoWallet');
      if (!encryptedWallet) throw new Error('Wallet data not found');
      const decrypted = await decryptData(encryptedWallet, password);
      const parsedData = JSON.parse(decrypted);
      console.log('Decrypted cryptoWallet data:', parsedData); // Debug log

      // Fetch and decrypt secure_v2_recoveryWords (optional)
      let mnemonic = parsedData.mnemonic || null;
      try {
        const encryptedRecoveryWords = await getFromStorage('secure_v2_recoveryWords');
        if (encryptedRecoveryWords) {
          const decryptedRecoveryWords = await decryptData(encryptedRecoveryWords, password);
          const parsedRecoveryWords = JSON.parse(decryptedRecoveryWords);
          console.log('Decrypted secure_v2_recoveryWords data:', parsedRecoveryWords); // Debug log
          if (Array.isArray(parsedRecoveryWords)) {
            const words = parsedRecoveryWords.filter(word => word.trim() !== '');
            if (words.length >= 12) {
              mnemonic = words.join(' ');
            } else {
              console.warn('Invalid recovery words: too few valid words');
            }
          } else if (typeof parsedRecoveryWords === 'string' && parsedRecoveryWords.trim()) {
            mnemonic = parsedRecoveryWords;
          }
        }
      } catch (err) {
        console.error('Error decrypting secure_v2_recoveryWords:', err);
        // Do not throw error; allow validation to succeed if cryptoWallet is valid
      }

      // Validate data
      if (!parsedData.privateKey && !mnemonic) {
        throw new Error('No private key or valid mnemonic found in wallet data');
      }

      return {
        ...parsedData,
        mnemonic
      };
    } catch (err) {
      console.error('Password validation error:', err);
      toast.error('Failed to validate password: ' + err.message, {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
      return null;
    }
  };

  const verify2FA = async () => {
    // Implementación real de 2FA iría aquí
    return twoFACode === '123456'; // Código de ejemplo
  };

  const generateBackup = async () => {
    if (!password) {
      toast.error('Please enter your wallet password', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
      return;
    }

    setIsGenerating(true);
    try {
      // Verificar 2FA si está habilitado
      const is2FAEnabled = await getFromStorage(`secure_v2_2faSecret_0x${wallet.address}`);
      if (is2FAEnabled) {
        setShow2FACode(true);
        return;
      }

      const decryptedWallet = await validatePassword();
      if (!decryptedWallet) {
        toast.error('Invalid password or wallet', {
          position: 'top-center',
          style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
        });
        return;
      }

      // Crear wallet desde privateKey si existe
      if (decryptedWallet.privateKey) {
        let privateKeyHex = decryptedWallet.privateKey;
        if (privateKeyHex.startsWith('0x')) {
          privateKeyHex = privateKeyHex.slice(2);
        }

        // Validar formato del privateKey
        if (!/^[0-9a-fA-F]{64}$/.test(privateKeyHex)) {
          throw new Error('Invalid private key format');
        }

        // Crear instancia de Wallet
        const walletInstance = Wallet.fromPrivateKey(Buffer.from(privateKeyHex, 'hex'));

        // Generar el V3 JSON
        const v3Json = await walletInstance.toV3(password, {
          kdf: 'scrypt',
          kdfparams: {
            n: 8192,
            r: 8,
            p: 1,
            dklen: 32,
            salt: Buffer.from(walletInstance.getAddressString()).slice(0, 32)
          },
          cipher: 'aes-128-ctr'
        });

        setBackupData(JSON.stringify(v3Json, null, 2));
      }

      setDecryptedData(decryptedWallet);
      toast.success('Backup generated successfully', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
    } catch (error) {
      console.error('Error generating backup:', error);
      toast.error(`Error: ${error.message}`, {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handle2FAVerify = async () => {
    if (!(await verify2FA())) {
      toast.error('Invalid 2FA code', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
      return;
    }

    setShow2FACode(false);
    await generateBackup();
  };

  const downloadBackup = () => {
    if (!backupData) {
      toast.error('No backup data to download', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
      return;
    }

    const element = document.createElement('a');
    const file = new Blob([backupData], { type: 'application/json' });
    element.href = URL.createObjectURL(file);
    element.download = `wallet-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('Backup downloaded successfully', {
      position: 'top-center',
      style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
    });
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Permanently delete wallet? This action cannot be undone.')) return;
    
    try {
      await clearSecureStorage();
      toast.success('Wallet deleted', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' },
        onClose: () => navigate('/create-wallet')
      });
    } catch (err) {
      console.error('DEBUG: Delete error:', err);
      toast.error('Error deleting wallet', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
    }
  };

  const handleLogout = async () => {
    try {
      await saveToStorage('sessionActive', 'false');
      toast.success('Logged out successfully', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' },
        onClose: () => navigate('/login')
      });
    } catch (err) {
      console.error('DEBUG: Error logging out:', err);
      toast.error('Error logging out', {
        position: 'top-center',
        style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
      });
    }
  };

  const menuItems = [
    { label: 'Dashboard', icon: <WalletIcon sx={{ color: '#ffffff' }} />, to: '/dashboard' },
    { label: 'Log Out', icon: <ExitToApp sx={{ color: '#ffffff' }} />, action: handleLogout },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#000000',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <ToastContainer 
        position="top-center" 
        limit={3}
        newestOnTop
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        draggable
        style={{ zIndex: 1400 }}
        toastStyle={{
          backgroundColor: '#1a1a1a',
          color: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)',
        }}
      />
      
      {/* Top bar */}
      <AppBar 
        position="sticky" 
        elevation={0} 
        sx={{ 
          bgcolor: '#1a1a1a',
          borderBottom: '1px solid #333333',
          boxShadow: 'none'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate(-1)} 
              sx={{ 
                color: '#ffffff',
                mr: 1,
                '&:hover': { backgroundColor: '#333333', color: '#cccccc' },
                '&:focus': { backgroundColor: '#333333', color: '#cccccc' },
                '&.Mui-focusVisible': { backgroundColor: '#333333', color: '#cccccc', outline: '2px solid #cccccc' }
              }}
              disableRipple
            >
              <ArrowBack sx={{ fontSize: '24px' }} />
            </IconButton>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: '#ffffff',
              fontSize: '1.4rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              letterSpacing: '2px',
              textTransform: 'uppercase'
            }}>
              AVO_STATION
            </Typography>
          </Box>
          <IconButton 
            onClick={handleMenuOpen}
            sx={{ 
              color: '#ffffff',
              '&:hover': { backgroundColor: '#333333', color: '#cccccc' },
              '&:focus': { backgroundColor: '#333333', color: '#cccccc' },
              '&.Mui-focusVisible': { backgroundColor: '#333333', color: '#cccccc', outline: '2px solid #cccccc' }
            }}
            disableRipple
          >
            <MenuIcon sx={{ fontSize: '24px' }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{ 
              sx: { 
                minWidth: 200,
                bgcolor: '#1a1a1a',
                borderRadius: '12px',
                border: '1px solid #333333',
                boxShadow: '0 6px 12px rgba(0, 0, 0, 0.4)'
              } 
            }}
          >
            {menuItems.map((item) => (
              <MenuItem
                key={item.label}
                onClick={() => {
                  handleMenuClose();
                  if (item.action) {
                    item.action();
                  } else {
                    navigate(item.to);
                  }
                }}
                sx={{ 
                  py: 1, 
                  fontSize: '1.0rem', 
                  color: '#ffffff',
                  '&:hover': { backgroundColor: '#333333', color: '#cccccc' }
                }}
              >
                <ListItemIcon sx={{ color: '#ffffff' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText sx={{ color: '#ffffff' }}>
                  {item.label}
                </ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Main content */}
      <Container maxWidth="md" sx={{ maxWidth: 800, px: 4, py: 4, flexGrow: 1 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Avatar sx={{ 
            bgcolor: '#333333', 
            color: '#ffffff',
            width: 64,
            height: 64,
            mb: 2,
            mx: 'auto'
          }}>
            <Security sx={{ fontSize: '32px' }} />
          </Avatar>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: '#ffffff', fontSize: '2.2rem' }}>
            Wallet Backup
          </Typography>
          <Typography variant="body1" color="#999999" sx={{ fontSize: '1.0rem' }}>
            Securely access your sensitive wallet information
          </Typography>
        </Box>

        <DashboardCard sx={{ mb: 3 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ 
              borderBottom: '1px solid #333333',
              '& .MuiTabs-indicator': { backgroundColor: '#ffffff' },
              '& .MuiTab-root': { 
                color: '#999999',
                fontSize: '1.0rem',
                textTransform: 'none',
                '&.Mui-selected': { color: '#ffffff' },
                '&:hover': { color: '#cccccc', backgroundColor: '#333333' }
              }
            }}
          >
            <Tab label="JSON Backup" icon={<CloudDownload sx={{ color: '#ffffff' }} />} />
            <Tab label="Private Key" icon={<VpnKey sx={{ color: '#ffffff' }} />} />
            <Tab label="Seed Phrase" icon={<FormatQuote sx={{ color: '#ffffff' }} />} />
          </Tabs>
        </DashboardCard>

        <DashboardCard sx={{ mb: 3, p: 3 }}>
          <TextField
            fullWidth
            label="Wallet Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Key sx={{ color: '#ffffff' }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#222222',
                '& fieldset': { borderColor: '#333333' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#ffffff' },
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.0rem',
                color: '#999999',
              },
              '& .MuiInputBase-input': {
                fontSize: '1.0rem',
                padding: '12px',
                color: '#ffffff'
              }
            }}
          />

          <GradientButton
            fullWidth
            size="large"
            onClick={generateBackup}
            disabled={isGenerating}
            startIcon={isGenerating ? <CircularProgress size={24} sx={{ color: '#000000' }} /> : <Security />}
            sx={{ py: 1.5, fontWeight: 600 }}
          >
            {isGenerating ? 'Verifying...' : 'Generate Backup'}
          </GradientButton>
        </DashboardCard>

        {/* Conditional content */}
        {isGenerating ? (
          <Fade in={isGenerating}>
            <DashboardCard sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress size={60} thickness={4} sx={{ color: '#ffffff', mb: 3 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff', fontSize: '1.4rem' }}>
                Verifying credentials...
              </Typography>
              <Typography variant="body2" color="#999999" sx={{ mt: 1, fontSize: '0.9rem' }}>
                Protecting your sensitive data
              </Typography>
            </DashboardCard>
          </Fade>
        ) : (
          <Fade in={!isGenerating}>
            <Box>
              {activeTab === 0 && backupData && (
                <DashboardCard sx={{ mb: 3 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Avatar sx={{ bgcolor: '#333333', color: '#ffffff' }}>
                        <CloudDownload />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff', fontSize: '1.4rem' }}>
                          MetaMask Compatible Backup
                        </Typography>
                        <Typography variant="body2" color="#999999" sx={{ fontSize: '0.9rem' }}>
                          V3 file ready to import
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <Box sx={{ 
                      bgcolor: '#222222',
                      p: 3,
                      borderRadius: '12px',
                      mb: 3,
                      maxHeight: 200,
                      overflowY: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      color: '#ffffff',
                      border: '1px solid #333333'
                    }}>
                      <pre style={{ margin: 0 }}>{backupData}</pre>
                    </Box>
                    
                    <GradientButton
                      fullWidth
                      size="large"
                      startIcon={<Save />}
                      onClick={downloadBackup}
                      sx={{ py: 1.5, fontWeight: 600 }}
                    >
                      Download Backup
                    </GradientButton>
                  </CardContent>
                </DashboardCard>
              )}

              {activeTab === 1 && decryptedData?.privateKey && (
                <DashboardCard sx={{ mb: 3 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Avatar sx={{ bgcolor: '#333333', color: '#ffffff' }}>
                        <VpnKey />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff', fontSize: '1.4rem' }}>
                          Private Key
                        </Typography>
                        <Typography variant="body2" color="#999999" sx={{ fontSize: '0.9rem' }}>
                          Keep this information secret!
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <TextField
                      fullWidth
                      value={decryptedData.privateKey}
                      type={showPrivateKey ? 'text' : 'password'}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPrivateKey(!showPrivateKey)}
                              sx={{ 
                                color: '#ffffff',
                                '&:hover': { backgroundColor: '#333333', color: '#cccccc' },
                                '&:focus': { backgroundColor: '#333333', color: '#cccccc' },
                                '&.Mui-focusVisible': { backgroundColor: '#333333', color: '#cccccc', outline: '2px solid #cccccc' }
                              }}
                              disableRipple
                            >
                              {showPrivateKey ? <VisibilityOff sx={{ fontSize: '20px' }} /> : <Visibility sx={{ fontSize: '20px' }} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ 
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: '#222222',
                          '& fieldset': { borderColor: '#333333' },
                          '&:hover fieldset': { borderColor: '#666666' },
                          '&.Mui-focused fieldset': { borderColor: '#ffffff' },
                        },
                        '& .MuiInputBase-input': {
                          fontSize: '1.0rem',
                          padding: '12px',
                          color: '#ffffff',
                          fontFamily: 'monospace'
                        }
                      }}
                    />
                    
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: '10px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)', display: 'inline-block' }}>
                        <QRCode 
                          value={decryptedData.privateKey} 
                          size={128}
                          level="H"
                          bgColor="#ffffff"
                          fgColor="#000000"
                        />
                      </Box>
                    </Box>
                    
                    <Button
                      fullWidth
                      variant="outlined"
                      size="large"
                      startIcon={<ContentCopy />}
                      onClick={() => {
                        navigator.clipboard.writeText(decryptedData.privateKey);
                        toast.success('Private key copied', {
                          position: 'top-center',
                          style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
                        });
                      }}
                      sx={{
                        borderRadius: '12px',
                        py: 1.5,
                        fontWeight: 500,
                        color: '#ffffff',
                        borderColor: '#333333',
                        '&:hover': { borderColor: '#666666', backgroundColor: '#333333' },
                        '&:focus': { borderColor: '#666666', backgroundColor: '#333333' },
                        '&.Mui-disabled': { borderColor: '#333333', color: '#666666' }
                      }}
                    >
                      Copy Key
                    </Button>
                  </CardContent>
                </DashboardCard>
              )}

              {activeTab === 2 && (
                <DashboardCard sx={{ mb: 3 }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <Avatar sx={{ bgcolor: '#333333', color: '#ffffff' }}>
                        <FormatQuote />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff', fontSize: '1.4rem' }}>
                          Seed Phrase (BIP39)
                        </Typography>
                        <Typography variant="body2" color="#999999" sx={{ fontSize: '0.9rem' }}>
                          Recovery words - Store them safely!
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <Box sx={{ 
                      bgcolor: '#222222',
                      p: 3,
                      borderRadius: '12px',
                      mb: 3,
                      textAlign: 'center',
                      border: '1px solid #333333'
                    }}>
                      {decryptedData?.mnemonic ? (
                        showSeedPhrase ? (
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#ffffff', fontSize: '1.0rem' }}>
                            {decryptedData.mnemonic}
                          </Typography>
                        ) : (
                          <Typography variant="body1" color="#999999" sx={{ fontSize: '1.0rem' }}>
                            ••••• ••••• ••••• ••••• ••••• ••••• ••••• ••••• ••••• ••••• ••••• •••••
                          </Typography>
                        )
                      ) : (
                        <Typography variant="body1" color="#ffffff" sx={{ fontSize: '1.0rem' }}>
                          Seed phrase not available. Ensure it is stored correctly in your wallet.
                        </Typography>
                      )}
                    </Box>
                    
                    {decryptedData?.mnemonic && (
                      <>
                        <GradientButton
                          fullWidth
                          size="large"
                          startIcon={showSeedPhrase ? <VisibilityOff /> : <Visibility />}
                          onClick={() => setShowSeedPhrase(!showSeedPhrase)}
                          sx={{ py: 1.5, fontWeight: 600, mb: 2 }}
                        >
                          {showSeedPhrase ? 'Hide Phrase' : 'Show Phrase'}
                        </GradientButton>
                        
                        <Button
                          fullWidth
                          variant="outlined"
                          size="large"
                          startIcon={<ContentCopy />}
                          onClick={() => {
                            if (showSeedPhrase) {
                              navigator.clipboard.writeText(decryptedData.mnemonic);
                              toast.success('Seed phrase copied', {
                                position: 'top-center',
                                style: { backgroundColor: '#1a1a1a', color: '#ffffff', borderRadius: '12px' }
                              });
                            }
                          }}
                          disabled={!showSeedPhrase}
                          sx={{
                            borderRadius: '12px',
                            py: 1.5,
                            fontWeight: 500,
                            color: '#ffffff',
                            borderColor: '#333333',
                            '&:hover': { borderColor: '#666666', backgroundColor: '#333333' },
                            '&:focus': { borderColor: '#666666', backgroundColor: '#333333' },
                            '&.Mui-disabled': { borderColor: '#333333', color: '#666666' }
                          }}
                        >
                          Copy Phrase
                        </Button>
                      </>
                    )}
                  </CardContent>
                </DashboardCard>
              )}
            </Box>
          </Fade>
        )}

        {/* Security warning */}
        <DashboardCard sx={{ bgcolor: '#222222', border: '1px solid #333333' }}>
          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" spacing={2}>
              <ErrorOutline sx={{ 
                color: '#ffffff',
                fontSize: '2rem'
              }} />
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, color: '#ffffff', fontSize: '1.0rem' }}>
                  Security Warning!
                </Typography>
                <Typography variant="body2" color="#999999" sx={{ fontSize: '0.9rem' }}>
                  • Never share your private key or seed phrase<br />
                  • Anyone with access can control your funds<br />
                  • Store this information offline in secure locations<br />
                  • Consider using a hardware wallet for better security
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </DashboardCard>
      </Container>

      {/* 2FA Dialog */}
      <Dialog open={show2FACode} onClose={() => setShow2FACode(false)} PaperProps={{ sx: { bgcolor: '#1a1a1a', borderRadius: '12px', border: '1px solid #333333' } }}>
        <DialogTitle sx={{ color: '#ffffff', fontSize: '1.4rem', fontWeight: 600 }}>Enter 2FA Code</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="2FA Code"
            type="number"
            fullWidth
            value={twoFACode}
            onChange={(e) => setTwoFACode(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TwoWheeler sx={{ color: '#ffffff' }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                backgroundColor: '#222222',
                '& fieldset': { borderColor: '#333333' },
                '&:hover fieldset': { borderColor: '#666666' },
                '&.Mui-focused fieldset': { borderColor: '#ffffff' },
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.0rem',
                color: '#999999',
              },
              '& .MuiInputBase-input': {
                fontSize: '1.0rem',
                padding: '12px',
                color: '#ffffff'
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShow2FACode(false)}
            sx={{
              borderRadius: '12px',
              fontWeight: 500,
              color: '#ffffff',
              '&:hover': { backgroundColor: '#333333', color: '#cccccc' },
              '&:focus': { backgroundColor: '#333333', color: '#cccccc' }
            }}
          >
            Cancel
          </Button>
          <GradientButton
            onClick={handle2FAVerify}
            sx={{ fontWeight: 600 }}
          >
            Verify
          </GradientButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ExportBackup;